export function debounce<T extends (...args: any[]) => any>(fn: T, ms: number): T & { cancel(): void; flush(): void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: any[] | null = null;

  const wrapped = ((...args: any[]) => {
    lastArgs = args;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...(lastArgs as any[]));
      lastArgs = null;
    }, ms);
  }) as T & { cancel(): void; flush(): void };

  wrapped.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    lastArgs = null;
  };
  wrapped.flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
      if (lastArgs) {
        fn(...lastArgs);
        lastArgs = null;
      }
    }
  };

  return wrapped;
}

/**
 * Coalescing debouncer that groups multiple keyed calls by the same key,
 * keeping the last args. Useful for per-section saves.
 */
export function keyedDebounce<Key extends string, Args extends any[]>(
  fn: (key: Key, ...args: Args) => void,
  ms: number
): ((key: Key, ...args: Args) => void) & { flushAll(): void; cancel(key: Key): void } {
  const timers = new Map<Key, { timer: ReturnType<typeof setTimeout>; args: Args }>();

  const wrapped = (key: Key, ...args: Args) => {
    const existing = timers.get(key);
    if (existing) clearTimeout(existing.timer);
    const timer = setTimeout(() => {
      timers.delete(key);
      fn(key, ...args);
    }, ms);
    timers.set(key, { timer, args });
  };

  (wrapped as any).flushAll = () => {
    for (const [key, { timer, args }] of timers) {
      clearTimeout(timer);
      fn(key, ...args);
    }
    timers.clear();
  };
  (wrapped as any).cancel = (key: Key) => {
    const existing = timers.get(key);
    if (existing) {
      clearTimeout(existing.timer);
      timers.delete(key);
    }
  };

  return wrapped as any;
}
